import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const BrandContext = createContext(null);
const STORAGE_KEY = "plania_brand";

// Convention :
//   - clé localStorage absente        → aucun choix explicite, on tombera sur brands[0]
//   - clé présente avec valeur ""     → choix explicite « Tous les départements » (multi-marques)
//   - clé présente avec valeur <uuid> → département spécifique
function readStoredBrand() {
  if (typeof window === "undefined") return { present: false, value: "" };
  const raw = localStorage.getItem(STORAGE_KEY);
  return { present: raw !== null, value: raw || "" };
}

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(() => readStoredBrand().value);

  const refresh = async () => {
    try {
      const { data } = await api.get("/brands");
      const list = data || [];
      setBrands(list);
      const { present, value } = readStoredBrand();
      // Cas 1 : aucune valeur stockée → défaut = premier département disponible
      if (!present) {
        const id = list[0]?.id || "";
        setSelectedBrandId(id);
        if (id) localStorage.setItem(STORAGE_KEY, id);
        return;
      }
      // Cas 2 : "" explicite → l'utilisateur a choisi Tous les départements, on le conserve
      if (value === "") {
        setSelectedBrandId("");
        return;
      }
      // Cas 3 : id spécifique → vérifier qu'il fait toujours partie du scope de l'utilisateur
      const valid = list.some((b) => b.id === value);
      if (!valid) {
        const id = list[0]?.id || "";
        setSelectedBrandId(id);
        if (id) localStorage.setItem(STORAGE_KEY, id);
        else localStorage.removeItem(STORAGE_KEY);
      } else if (!selectedBrandId) {
        setSelectedBrandId(value);
      }
    } catch {
      setBrands([]);
    }
  };

  useEffect(() => {
    if (user && user !== false) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selectBrand = (id) => {
    setSelectedBrandId(id);
    // On stocke explicitement même la valeur vide pour préserver le choix "Tous"
    localStorage.setItem(STORAGE_KEY, id || "");
  };

  return (
    <BrandContext.Provider value={{ brands, selectedBrandId, selectBrand, refresh }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrands = () => useContext(BrandContext);
